import { BaseService } from './BaseService'
import { ScoutAccountsResponse, ScoutScanStatusResponse } from '../types/scout'

export interface ScoutBrandHint {
  id: string
  customer_id: string
  scout_account_id: string | null
  scout_account_username?: string | null
  brand_name: string
  brand_aliases: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ScoutScanError {
  id: string
  scan_id: string
  scout_post_id: number | null
  platform_post_id: string | null
  stage: string
  error_code: string | null
  error_text: string
  created_at: string
}

interface BrandHintsResponse {
  success: boolean
  data: ScoutBrandHint[]
}
interface BrandHintResponse {
  success: boolean
  data: ScoutBrandHint
  message?: string
}
interface ScanTriggerResponse {
  success: boolean
  data: { scanId: string | null }
  message?: string
}
interface ScanErrorsResponse {
  success: boolean
  data: ScoutScanError[]
}

/**
 * Admin-only Brand Scout operations (brand-hint watchlist + reanalyze + scan errors).
 * Surfaced in the Support → organization drawer; all calls go to the CRO service and
 * are gated server-side on the access:support permission.
 */
class ScoutAdminService extends BaseService {
  constructor() {
    super('cro')
  }

  // ── Accounts / scan status (support-gated, no read:own_data needed) ────────
  async listAccounts(customerId: string): Promise<ScoutAccountsResponse> {
    return this.get<ScoutAccountsResponse>('/scout/accounts/admin/accounts', {
      customerId,
      page: 1,
      limit: 100
    })
  }

  async getScanStatus(scanId: string, customerId: string): Promise<ScoutScanStatusResponse> {
    return this.get<ScoutScanStatusResponse>(`/scout/accounts/admin/scans/${scanId}/status`, {
      customerId
    })
  }

  // ── Scout account admin management (support-only flags) ────────────────────
  async createAccount(
    customerId: string,
    payload: { username: string; platformId?: number; is_owned?: boolean; video_analysis_enabled?: boolean }
  ): Promise<{ success: boolean; data: any }> {
    return this.post<{ success: boolean; data: any }>('/scout/accounts/admin/accounts', payload, { customerId })
  }

  async updateAccount(
    id: string,
    customerId: string,
    updates: { is_owned?: boolean; video_analysis_enabled?: boolean; is_active?: boolean; display_name?: string }
  ): Promise<{ success: boolean; data: any }> {
    return this.put<{ success: boolean; data: any }>(`/scout/accounts/admin/accounts/${id}`, updates, { customerId })
  }

  // Trigger a full fresh scrape + analyze for an account (downloads fresh media incl. video).
  async rescanAccount(id: string, customerId: string, daysAgo = 30): Promise<ScanTriggerResponse> {
    return this.post<ScanTriggerResponse>(`/scout/accounts/admin/accounts/${id}/rescan`, { daysAgo }, { customerId })
  }

  // ── Brand hints ──────────────────────────────────────────────────────────
  async listBrandHints(customerId: string, scoutAccountId?: string): Promise<BrandHintsResponse> {
    return this.get<BrandHintsResponse>('/scout/brand-hints', { customerId, scoutAccountId })
  }

  async createBrandHint(
    customerId: string,
    payload: { brandName: string; brandAliases?: string[]; scoutAccountId?: string | null }
  ): Promise<BrandHintResponse> {
    return this.post<BrandHintResponse>('/scout/brand-hints', payload, { customerId })
  }

  async updateBrandHint(
    id: string,
    customerId: string,
    updates: { brandName?: string; brandAliases?: string[]; isActive?: boolean }
  ): Promise<BrandHintResponse> {
    return this.put<BrandHintResponse>(`/scout/brand-hints/${id}`, updates, { customerId })
  }

  async deleteBrandHint(id: string, customerId: string): Promise<{ success: boolean; message: string }> {
    return this.delete<{ success: boolean; message: string }>(`/scout/brand-hints/${id}`, { customerId })
  }

  // ── Reanalyze ──────────────────────────────────────────────────────────────
  async reanalyzeAccount(accountId: string, customerId: string, daysAgo: number): Promise<ScanTriggerResponse> {
    return this.post<ScanTriggerResponse>(`/scout/accounts/${accountId}/reanalyze`, { daysAgo }, { customerId })
  }

  async reanalyzePost(platformPostId: string, customerId: string): Promise<ScanTriggerResponse> {
    // platformPostId may contain characters that need encoding in the path segment.
    return this.post<ScanTriggerResponse>(
      `/scout/accounts/posts/${encodeURIComponent(platformPostId)}/reanalyze`,
      {},
      { customerId }
    )
  }

  // ── Scan errors ──────────────────────────────────────────────────────────
  async getScanErrors(scanId: string, customerId: string): Promise<ScanErrorsResponse> {
    return this.get<ScanErrorsResponse>(`/scout/accounts/scans/${scanId}/errors`, { customerId })
  }
}

export const scoutAdminService = new ScoutAdminService()
export default scoutAdminService
